// Marketing-attribution capture: stores first-touch (write-once) and last-touch
// (overwritten on each new ad click, with a 30-day TTL) in localStorage. Reads
// `utm_*`, `gclid`, `gbraid`, `wbraid`, `fbclid`, `loclid` from the page URL on
// every navigation. Forms read these on submit so the lead doc carries the
// original ad context all the way to HubSpot/Loops/CAPI.
//
// `loclid` ("Loops click ID") is our own newsletter-tracking param: the
// marketing lead appends `?loclid=<id>` to links inside Loops newsletters, the
// same way Google appends `gclid` and Meta appends `fbclid`. It flows through
// the identical pipeline so newsletter clicks get attributed to the
// "newsletter" source channel and convert through to HubSpot + Loops.

import { hasMarketingConsent } from "./pixels";

const STORAGE_KEY_FIRST = "scaffcalc.ad_first_touch";
const STORAGE_KEY_LAST = "scaffcalc.ad_last_touch";

const LAST_TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const AD_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "loclid"
] as const;

type AdParamKey = (typeof AD_PARAM_KEYS)[number];

export type Attribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  loclid?: string;
  referrer?: string;
  landing_path: string;
  ts: number;
};

function readAdParamsFromUrl(): Partial<Attribution> {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const result: Partial<Attribution> = {};
  for (const key of AD_PARAM_KEYS) {
    const v = params.get(key);
    if (v) (result as Record<string, string>)[key] = v;
  }
  return result;
}

function isAdClick(p: Partial<Attribution>): boolean {
  return AD_PARAM_KEYS.some((k: AdParamKey) => Boolean(p[k]));
}

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function getStoredFirstTouch(): Attribution | null {
  if (typeof window === "undefined") return null;
  return safeParse<Attribution>(localStorage.getItem(STORAGE_KEY_FIRST));
}

export function getStoredLastTouch(): Attribution | null {
  if (typeof window === "undefined") return null;
  const parsed = safeParse<Attribution>(localStorage.getItem(STORAGE_KEY_LAST));
  if (!parsed) return null;
  if (Date.now() - parsed.ts > LAST_TOUCH_TTL_MS) {
    localStorage.removeItem(STORAGE_KEY_LAST);
    return null;
  }
  return parsed;
}

// Reads the current URL for ad params and writes first-/last-touch to
// localStorage. No-op if the URL has no ad params. Returns the snapshot for
// downstream callers (e.g. PostHog enrichment) so they don't need a second
// read.
export function captureAttribution(): {
  first: Attribution | null;
  last: Attribution | null;
} {
  if (typeof window === "undefined") {
    return { first: null, last: null };
  }

  const params = readAdParamsFromUrl();
  if (isAdClick(params)) {
    const fresh: Attribution = {
      ...params,
      referrer: document.referrer || undefined,
      landing_path: window.location.pathname,
      ts: Date.now()
    };

    if (!localStorage.getItem(STORAGE_KEY_FIRST)) {
      localStorage.setItem(STORAGE_KEY_FIRST, JSON.stringify(fresh));
    }
    localStorage.setItem(STORAGE_KEY_LAST, JSON.stringify(fresh));
  }

  return {
    first: getStoredFirstTouch(),
    last: getStoredLastTouch()
  };
}

// Used when marketing consent is revoked — drop everything we stored.
export function clearAttribution() {
  if (typeof window === "undefined") return;
  localStorage.removeItem(STORAGE_KEY_FIRST);
  localStorage.removeItem(STORAGE_KEY_LAST);
}

// Meta's own first-party cookies, set by fbevents.js once marketing consent
// mounts the pixel, plus the client context Meta requires on events sent with
// `action_source: "website"`.
//
// These are read, never derived. `_fbp` is a random id minted by the pixel, so
// it cannot be reconstructed server-side at all — a fabricated value matches no
// profile at Meta and drags match quality down instead of up. `_fbc` *can* be
// rebuilt from a stored `fbclid`, but only the real cookie carries the timestamp
// of the actual click, so we pass it through and let the server reconstruct
// purely as a fallback.
export type MetaBrowserContext = {
  fbp?: string;
  fbc?: string;
  client_user_agent: string;
  event_source_url: string;
};

// Returned verbatim, without decodeURIComponent: `_fbp`/`_fbc` are
// `fb.<n>.<ms>.<id>` values that never need decoding, and a malformed
// percent-escape in any cookie on this domain would throw a URIError straight
// out of the form-submit handler.
function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  for (const part of document.cookie.split("; ")) {
    const eq = part.indexOf("=");
    if (eq > 0 && part.slice(0, eq) === name) {
      return part.slice(eq + 1);
    }
  }
  return undefined;
}

// Null when marketing consent is absent, or when neither cookie exists because
// the pixel never ran.
//
// Cookie presence alone is NOT a consent check. It proves consent was granted
// once — nothing else on this domain sets these — but `_fbp`/`_fbc` live 90 days
// and outlive a withdrawal, so a de-consented visitor still has them in the jar.
// Reading them then would be worse than the fallback it replaces: withdrawal
// clears the stored attribution (see clearAttribution, wired to
// CookiebotOnDecline in AttributionTracker) but not the cookies, so we would
// send Meta *stronger* identifiers after a withdrawal than before it. Hence the
// same per-use re-check every other pixel caller does.
export function getMetaBrowserContext(): MetaBrowserContext | null {
  if (typeof window === "undefined") return null;
  if (!hasMarketingConsent()) return null;
  const fbp = readCookie("_fbp");
  const fbc = readCookie("_fbc");
  if (!fbp && !fbc) return null;
  return {
    ...(fbp ? { fbp } : {}),
    ...(fbc ? { fbc } : {}),
    client_user_agent: navigator.userAgent,
    // Origin + path only. Meta needs the URL to attribute the page; the query
    // and fragment are not required and would ship whatever a future prefilled
    // link happens to put there.
    event_source_url: window.location.origin + window.location.pathname
  };
}

// Form-submit helper: snapshot of current attribution, suitable for spreading
// into a Firestore lead doc. Excluded fields stay absent (not undefined) so
// they don't trip the `data.size()` rule in firestore.website.rules.
export function getAttributionPayload(): {
  firstTouch?: Attribution;
  lastTouch?: Attribution;
  metaContext?: MetaBrowserContext;
} {
  const first = getStoredFirstTouch();
  const last = getStoredLastTouch();
  const metaContext = getMetaBrowserContext();
  return {
    ...(first ? { firstTouch: first } : {}),
    ...(last ? { lastTouch: last } : {}),
    ...(metaContext ? { metaContext } : {})
  };
}

export function generateEventId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for very old browsers — extremely unlikely in our supported set
  // but keeps the function total. Not cryptographically strong.
  return "ev-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
